import { useSelector } from "react-redux";
import { useModuleBEM } from "@b2bportal/core-themes";
import { useI18nContext } from "@hopper-b2b/i18n";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Icon,
} from "@tenant/ui";
import clsx from "clsx";
import styles from "./GridV2MinimumRedemptionBanner.module.scss";
import { AccountSelectors } from "@/modules/account";
import { TenantIconNameEnum } from "@/types/tenantIcons";
import { useIsExchangeFlow } from "@/utilities/flights/useIsExchangeFlow";

const ACCORDION_ITEM = "grid-v2-minimum-redemption";

/**
 * Grid V2 counterpart of PointsEligibilityBanner: tells a shopper whose
 * balance is under the account's minimum to redeem anything why the results
 * show cash prices only. Open on arrival; collapsing keeps icon and title.
 * V1 keeps its own banner untouched.
 */
export const GridV2MinimumRedemptionBanner = ({
  className,
}: {
  className?: string;
}) => {
  const { t, formatNumber } = useI18nContext();
  const [block, cn] = useModuleBEM(styles, "GridV2MinimumRedemptionBanner");
  const isExchangeFlow = useIsExchangeFlow();

  const hasMinimumRedemptionPoints = useSelector(
    AccountSelectors.getHasMinimumRedemptionPoints
  );
  const minimumRedemptionPoints = useSelector(
    AccountSelectors.getMinimumRedemptionPoints
  );

  if (
    hasMinimumRedemptionPoints ||
    !minimumRedemptionPoints ||
    isExchangeFlow
  ) {
    return null;
  }

  return (
    <div className={clsx(block, className)}>
      <Accordion
        type="single"
        collapsible
        defaultValue={ACCORDION_ITEM}
        className={cn("accordion")}
      >
        <AccordionItem value={ACCORDION_ITEM} className={cn("item")}>
          <AccordionTrigger className={cn("trigger")}>
            <span className={cn("icon")}>
              <Icon iconName={TenantIconNameEnum.pointsCreditCard} />
            </span>
            <span className={cn("title")}>
              {t("gridV2.minimumRedemptionBanner.title", {
                minimum: formatNumber(minimumRedemptionPoints),
              })}
            </span>
          </AccordionTrigger>
          <AccordionContent className={cn("content")}>
            <p className={cn("body")}>
              {t("gridV2.minimumRedemptionBanner.body")}
            </p>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
};
